import { Box, Flex, Icon, Spinner, useToast } from '@chakra-ui/react'
import React from 'react'
import { MdFileDownload } from 'react-icons/md'
import { Button } from '../../../components/Button'
import { IoIosPaper } from 'react-icons/io'
import { UnificarFrete } from './components/Modais/UnificarFrete'
import { useMemo } from 'react'
import { formatPrice } from '../../../utils/formatPrice'
import { api } from '../../../services/api'
import { useMutation, useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useOrcamentoPedido } from '../../../contexts/OrcamentoPedidoProvider'
import { Skeleton } from './styles'
import { MarcarPerdido } from './components/Alerts/MarcarPerdido'
import { EnviarProposta } from './components/Alerts/EnviarProposta'
import { SolicitarST } from './components/Alerts/SolicitarST'

async function gerarPedido({ numeroNota }) {
  const response = await api.post('/site/v2/orcamento-crm/gerar-pedido-crm', {
    numero_nota: numeroNota,
  })

  return response.data
}

export const Footer = ({ numeroNota }) => {
  const toast = useToast({
    position: 'top-right',
    duration: 12000,
    isClosable: true,
  })

  const { isLoading: orcamentoLoading, infoProposta, pedidoBloqueado, itensPermitidos: listItems } = useOrcamentoPedido()

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const totalValue = useMemo(() => {
    return listItems.reduce(
      (acc, item) => {
        const { original_valor_total_itens, transportadora } = item
        const parsedValue = parseFloat(original_valor_total_itens)

        const parsedFrete = parseFloat(transportadora?.valor_simulacao || 0)

        return {
          total: acc.total + parsedValue,
          totalFrete: acc.totalFrete + parsedFrete,
        }
      },
      { total: 0, totalFrete: 0 }
    )
  }, [listItems])

  const valueFrete = infoProposta?.transportadora?.valor_simulacao ?? totalValue?.totalFrete

  function capitalizeFirstLetter(string) {
    if (!string) return
    return `${string.charAt(0).toUpperCase()}${string.slice(1)}`
  }

  const { mutateAsync, isLoading: gerarPedidoLoading } = useMutation({
    mutationFn: gerarPedido,
    onSuccess: (data) => {
      toast({
        title: 'Pedido gerado com sucesso',
        status: 'success',
      })

      queryClient.setQueryData(['items', numeroNota], (oldData) => {
        return {
          ...oldData,
          informacao_da_proposta: {
            ...oldData.informacao_da_proposta,
            valida_bloqueio: true,
          },
        }
      })

      navigate(`/vendas/gerar-pedido/${numeroNota}`)
    },
    onError: (error) => {
      toast({
        title: 'Erro ao gerar pedido',
        description: capitalizeFirstLetter(error['response']['data'].erros?.toLowerCase()),
        status: 'error',
      })
    },
  })

  async function handleGerarPedido({ numeroNota }) {
    const hasLayout = listItems.every((item) => item.layout_aprovado)
    const hasTrans = infoProposta?.transportadora?.nome_transportadora

    if (!hasTrans) {
      toast({
        title: 'Não é possível gerar pedido',
        description: 'É necessário unificar o frete para gerar o pedido.',
        status: 'error',
      })
      return
    }

    if (!hasLayout) {
      toast({
        title: 'Não é possível gerar pedido',
        description: 'É necessário aprovar os layouts para gerar o pedido.',
        status: 'error',
      })
      return
    }

    await mutateAsync({ numeroNota })
  }

  const totalOrcamento = useMemo(() => {
    return totalValue.total + parseFloat(infoProposta?.vlr_subs) + parseFloat(valueFrete)
  }, [totalValue.total, infoProposta?.vlr_subs, valueFrete])

  if (orcamentoLoading) {
    return (
      <Box as="footer" position="sticky" bottom="0" w="full" bg="white" maxW="1366px" px={4} mx="auto">
        <Skeleton width="100%" height="30px" background="#378BBF" />
        <Skeleton width="100%" height="76px" />
      </Box>
    )
  }

  return (
    <Box as="footer" position="sticky" bottom="0" w="full" bg="white" maxW="1366px" px={4} mx="auto">
      <Flex w="full" alignItems="center" justifyContent="space-between" bg="#378BBF" color="white" fontSize="13px" py="7px" px="20px" mx="auto">
        <Box>
          Orçamento: <strong>{listItems?.length} ITENS</strong>
        </Box>
        <Box>
          Valor Total Dos Itens: <strong>{formatPrice(totalValue.total)}</strong>
        </Box>
        <Box>
          Valor Total Frete {infoProposta?.transportadora ? '(Unificado)' : '(Não Unificado)'}: <strong>{formatPrice(valueFrete)}</strong>
        </Box>
        <Box>
          Valor Total ST: <strong>{!isNaN(parseFloat(infoProposta?.vlr_subs)) ? formatPrice(parseFloat(infoProposta?.vlr_subs)) : 'R$ 0,00'}</strong>
        </Box>
        <Box>
          Valor Total Orçamento: <strong>{!isNaN(totalOrcamento) ? formatPrice(totalOrcamento) : 'R$ 0,00'}</strong>
        </Box>
      </Flex>

      <Flex w="full" alignItems="center" justifyContent="space-between" fontSize="15px" py={4}>
        <Flex alignItems="center" gap={3} pointerEvents={pedidoBloqueado ? 'none' : 'auto'}>
          <UnificarFrete />
          <Button
            display="flex"
            alignItems="center"
            bgColor="transparent"
            fontWeight="normal"
            color="black"
            gap={2}
            transition="all 0.2s"
            _hover={{
              bg: '#e2e2e2',
            }}
            px={3}
            disabled={infoProposta?.valida_proposta_nova}
            py={2}
            cursor="pointer"
            as="a"
            href={`${process.env.REACT_APP_BASE_URL}/site/v2/orcamento/download-pdf-sankhya/${numeroNota}`}
            download
          >
            <Icon as={MdFileDownload} fontSize="20px" color="#378BBF" />
            Baixar Proposta
          </Button>
          <EnviarProposta />
          {/* <Comissoes numeroNota={numeroNota} infoProposta={infoProposta} /> */}
          Saldo restante comissão: {formatPrice(infoProposta?.comissao_prevista)}
          <MarcarPerdido />
          <SolicitarST />
        </Flex>
        {/* <VoltarProposta /> */}
        {infoProposta?.perdido && <Button bgColor="#FF0000">Pedido Perdido</Button>}
        {!infoProposta?.perdido && (
          <Button as={Link} to={`/vendas/gerar-pedido/${numeroNota}`} display={pedidoBloqueado ? 'flex' : 'none'}>
            Pedido Gerado - Acessar
          </Button>
        )}
        {!infoProposta?.perdido && (
          <Button
            disabled={infoProposta?.valida_proposta_nova}
            alignItems="center"
            bgColor="transparent"
            color="black"
            fontWeight="normal"
            border="1px solid #FFA200"
            gap={2}
            transition="all 0.2s"
            _hover={{
              bg: '#e2e2e2',
            }}
            px={3}
            py={2}
            pointerEvents={pedidoBloqueado ? 'none' : 'auto'}
            cursor="pointer"
            leftIcon={<Icon as={IoIosPaper} fontSize="20px" color="#FFA200" />}
            onClick={() => handleGerarPedido({ numeroNota })}
            isLoading={gerarPedidoLoading}
            display={!pedidoBloqueado ? 'flex' : 'none'}
            loadingText="Gerando Pedido..."
          >
            Gerar Pedido
          </Button>
        )}
      </Flex>
    </Box>
  )
}
